/**
 * Commissions Service
 * Handles API calls for commission tracking and management.
 * Uses the shared apiClient (Axios) for auth, CSRF, and credentials.
 * Preserves TTL caching and batch-with-delay processing for trip commissions.
 */

import apiClient from './apiClient';

class CommissionsService {
  constructor() {
    this.requestCache = new Map();
    this.cacheTimeout = 30000; // 30-second TTL
  }

  async _request(method, endpoint, data = null, signal = null) {
    const config = {
      // Axios timeout equivalent of AbortSignal.timeout(5000)
      timeout: 5000,
      ...(signal ? { signal } : {}),
    };

    try {
      const response =
        method === 'GET'
          ? await apiClient.get(endpoint, config)
          : method === 'POST'
          ? await apiClient.post(endpoint, data, config)
          : method === 'PUT'
          ? await apiClient.put(endpoint, data, config)
          : await apiClient.delete(endpoint, config);

      const responseData = response.data;
      return {
        success: responseData.success !== undefined ? responseData.success : true,
        data: responseData.data ?? responseData,
      };
    } catch (error) {
      // Axios timeout throws with code 'ECONNABORTED'; AbortError is name 'CanceledError'
      if (error.code === 'ECONNABORTED' || error.name === 'CanceledError') {
        throw error; // Let callers handle silently
      }
      console.error('[CommissionsService] Request failed:', error);
      throw error;
    }
  }

  async getTripCommission(tripId) {
    try {
      const cacheKey = `trip_${tripId}`;
      const cached = this.requestCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }

      const result = await this._request('GET', `/api/trips/${tripId}/commission`);

      this.requestCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    } catch {
      return { success: false, data: null };
    }
  }

  async getTripCommissionsBatch(tripIds, batchSize = 5, delayBetweenBatches = 200) {
    const results = new Map();

    for (let i = 0; i < tripIds.length; i += batchSize) {
      const batch = tripIds.slice(i, i + batchSize);

      await Promise.all(
        batch.map(async (tripId) => {
          try {
            const commission = await this.getTripCommission(tripId);
            if (commission.success && commission.data) {
              results.set(tripId, commission.data);
            }
          } catch {
            console.debug(`Skipping commission for trip ${tripId}`);
          }
        })
      );

      if (i + batchSize < tripIds.length) {
        await new Promise((resolve) => setTimeout(resolve, delayBetweenBatches));
      }
    }

    return results;
  }

  async getAllCommissions(filters = {}) {
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== '')
      );
      const { data } = await apiClient.get('/api/commission/tracking', { params, timeout: 5000 });
      return {
        success: data.success !== undefined ? data.success : true,
        data: data.data ?? data,
      };
    } catch (error) {
      console.error('[CommissionsService] getAllCommissions failed:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  async getCommissionSummary(period = 'current_month') {
    try {
      return await this._request('GET', `/api/commission/summary?period=${period}`);
    } catch (error) {
      console.error('[CommissionsService] getCommissionSummary failed:', error);
      return { success: false, data: { total: 0, pending: 0, paid: 0, total_amount: 0 }, error: error.message };
    }
  }

  async getCommissionAnalytics(period = 'current_month') {
    try {
      return await this._request('GET', `/api/commission/analytics?period=${period}`);
    } catch (error) {
      console.error('[CommissionsService] getCommissionAnalytics failed:', error);
      return { success: false, data: null, error: error.message };
    }
  }

  async updateCommission(tripId, commissionData) {
    return this._request('PUT', `/api/trips/${tripId}/commission`, commissionData);
  }

  async createCommissionBooking(commissionData) {
    return this._request('POST', '/api/commission/booking', commissionData);
  }

  async updateCommissionBooking(bookingId, commissionData) {
    return this._request('PUT', `/api/commission/booking/${bookingId}`, commissionData);
  }

  async deleteCommissionBooking(bookingId) {
    return this._request('DELETE', `/api/commission/booking/${bookingId}`);
  }

  clearCache() {
    this.requestCache.clear();
  }

  clearTripCache(tripId) {
    this.requestCache.delete(`trip_${tripId}`);
  }
}

export default new CommissionsService();
